'use client';

import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { PlantCard } from '@/hooks/use-plants';
import { api } from '@/lib/utils/api';
import { useAuthStore } from '@/stores/auth-store';

export type PlantDeviceStato = 'ok' | 'warn' | 'fault' | 'offline';

export interface PlantInverter {
	id: string;
	plantId: string;
	codice: string;
	potenzaKw: number | null;
	marca: string | null;
	modello: string | null;
	stringheTotali: number | null;
	stato: PlantDeviceStato;
	ultimoContattoAt: string | null;
	liveKw: number;
	/** Performance Ratio del giorno richiesto (0-1). */
	pr: number | null;
}

export interface PlantCurvePoint {
	ora: string;
	ac: number;
	dc: number;
}

export interface PlantDailyPoint {
	giorno: string;
	energiaKwh: number | null;
	pr: number | null;
}

export interface PlantTypicalDaySeries {
	label: 'Picco' | 'Media' | 'Peggiore';
	giorno: string;
	punti: { ora: string; ac: number }[];
}

export interface PlantDetail {
	plant: PlantCard;
	inverters: PlantInverter[];
	/** Curva DC/AC di oggi — solo sul mese in corso, vedi `curvaTipo`. */
	curva: PlantCurvePoint[];
	/** Tre giornate tipo del mese richiesto, stimate — solo sui mesi passati. */
	curvaTipo: PlantTypicalDaySeries[] | null;
	/** Giorno della curva DC/AC, sempre oggi (UTC). */
	giornoCurva: string;
	/** Giorno dei KPI, di `poaWm2` e `tModuliC`, `YYYY-MM-DD`. */
	giorno: string;
	mensile: PlantDailyPoint[];
	poaWm2: number | null;
	tModuliC: number | null;
}

export interface PlantOption {
	id: string;
	nome: string;
	localita: string | null;
}

const EMPTY_OPTIONS: PlantOption[] = [];

/**
 * La scheda di un impianto (`GET /api/plants/:id`) più l'elenco per il
 * selettore (`GET /api/plants/options`).
 *
 * Le opzioni si caricano anche quando nessun impianto è selezionato: senza, il
 * selettore resterebbe vuoto finché non si sceglie qualcosa, cioè per sempre.
 *
 * ADR-006: backed by React Query — due query distinte (opzioni indipendenti dal
 * `plantId` selezionato).
 */
export function usePlantDetail(
	orgId: string | null | undefined,
	plantId: string | null | undefined,
	month?: string,
) {
	const loaded = useAuthStore((s) => s.loaded);
	const scopeReady = loaded && !!orgId;

	const optionsQuery = useQuery({
		queryKey: ['plant-options', orgId],
		enabled: scopeReady,
		queryFn: () => api.get<PlantOption[]>('/api/plants/options', { orgId: orgId as string }),
	});

	const detailActive = scopeReady && !!plantId;
	const { data, isLoading, error, refetch: rqRefetch } = useQuery({
		queryKey: ['plant-detail', orgId, plantId, month ?? null],
		enabled: detailActive,
		// Solo sul mese in corso la curva è telemetria live che si muove; su un
		// mese passato è già consolidata, ripeterla ogni minuto non cambierebbe nulla.
		refetchInterval: month ? false : 60_000,
		queryFn: () =>
			api.get<PlantDetail>(
				`/api/plants/${plantId}`,
				month ? { orgId: orgId as string, month } : { orgId: orgId as string },
			),
	});

	const refetch = useCallback(async () => {
		await rqRefetch();
	}, [rqRefetch]);

	return {
		detail: data ?? null,
		options: Array.isArray(optionsQuery.data) ? optionsQuery.data : EMPTY_OPTIONS,
		isLoading: detailActive ? isLoading : false,
		error: error
			? error instanceof Error
				? error.message
				: "Errore nel caricamento dell'impianto"
			: null,
		refetch,
	};
}
